import * as React from "react";
import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSessionIdentityKey } from "@authe/ui-core/session";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { useStorageIdentity } from "@/app/storageIdentity";
import type { QuickStartWork } from "./quickStartModel";

const EMPTY: QuickStartWork = {};
const HINT_KEY = "quick-start:work-hint";
const HINT_MAX_AGE = 5 * 60_000;
const keyFor = (identity: string | undefined) => ["quick-start-work", identity] as const;

/** Persist counts only: no case identifiers, titles or member details. */
export function readWorkHint(raw: unknown, identity: string | undefined, now = Date.now()): QuickStartWork | undefined {
  if (!identity || !raw || typeof raw !== "object") return undefined;
  const hint = raw as Record<string, unknown>;
  if (hint.identity !== identity || typeof hint.at !== "number" || hint.at > now || now - hint.at > HINT_MAX_AGE) return undefined;
  const data: QuickStartWork = {};
  for (const kind of ["cases", "watches"] as const) {
    const count = hint[kind];
    if (typeof count === "number" && Number.isSafeInteger(count) && count >= 0) data[kind] = { count };
  }
  return data;
}

function useWorkStorage() {
  const { app, customerId, orgId, tenantId, userId } = useStorageIdentity();
  return React.useMemo(() => userId ? createScopedStorage({ app, customerId, orgId, tenantId, userId }) : null,
    [app, customerId, orgId, tenantId, userId]);
}

/** Observe facts already fetched by the queue; never start another inventory read. */
export function useQuickStartWork(): QuickStartWork {
  const identity = useSessionIdentityKey();
  const storage = useWorkStorage();
  const hint = React.useMemo(() => readWorkHint(storage?.getAt(HINT_KEY, "user", null), identity), [storage, identity]);
  const query = useQuery<QuickStartWork>({ queryKey: keyFor(identity), queryFn: skipToken, initialData: hint });
  return identity ? query.data ?? EMPTY : EMPTY;
}

/** Failed/unavailable reads remove the hint; loading preserves the last known facts. */
export function useReportQuickStartWork<K extends keyof QuickStartWork>(kind: K, value: QuickStartWork[K], settled = true): void {
  const client = useQueryClient();
  const identity = useSessionIdentityKey();
  const storage = useWorkStorage();
  const serialized = JSON.stringify(value);
  React.useEffect(() => {
    if (!identity || !settled) return;
    const fact = serialized === undefined ? undefined : JSON.parse(serialized) as QuickStartWork[K];
    const previous = client.getQueryData<QuickStartWork>(keyFor(identity));
    if (JSON.stringify(previous?.[kind]) === serialized) return;
    const next = { ...previous, [kind]: fact };
    client.setQueryData<QuickStartWork>(keyFor(identity), next);
    storage?.set(HINT_KEY, { identity, at: Date.now(), cases: next.cases?.count, watches: next.watches?.count }, "user");
  }, [client, identity, kind, serialized, settled, storage]);
}
